export const ENGINE_TIMESTAMP = "2026-06-21T00:00:00.000Z";
export const ENGINE_VERSION = "wave1b-2026-06-21";
export const NO_RISK = {
    level: "none",
    riskRefs: [],
    notes: "No risk identified for this derived item."
};
export function deriveStrategyContext(projectId, apartmentTypeId, takeoffRows, requirements, catalogReleaseId, rules, nonDrawingTakeoffSourceRefs = []) {
    const approvedRequirements = requirements.filter((requirement) => requirement.status === "approved");
    const sourceRefs = uniqueSourceRefs([
        ...nonDrawingTakeoffSourceRefs,
        ...approvedRequirements.flatMap((requirement) => requirement.sourceRefs),
        ...rules.sourceRefs
    ]);
    const requirementText = approvedRequirements
        .map((requirement) => `${requirement.type} ${requirement.interpretedText}`)
        .join("\n")
        .toLowerCase();
    const rawTakeoffText = takeoffRows
        .map((row) => JSON.stringify(row.fieldValues))
        .join("\n")
        .toLowerCase();
    const meteringEvidenceText = `${requirementText}\n${rawTakeoffText}`;
    const edgeFeatures = detectEdgeFeatures(approvedRequirements);
    const edgeRequirementIds = new Set(edgeFeatures.flatMap((feature) => feature.sourceRequirementIds));
    const nonEdgeRequirementText = approvedRequirements
        .filter((requirement) => !edgeRequirementIds.has(requirement.id))
        .map((requirement) => `${requirement.type} ${requirement.interpretedText}`)
        .join("\n")
        .toLowerCase();
    const rowCentralScreens = takeoffRows.reduce((total, row) => total + (row.counts.centralControlScreenCount ?? 0), 0);
    const uiStrategy = deriveUiStrategy({
        projectId,
        apartmentTypeId,
        requirements: approvedRequirements,
        takeoffRows,
        rowCentralScreens,
        sourceRefs
    });
    const meterEndpointCount = takeoffRows.reduce((total, row) => total +
        (row.counts.hiuCount ?? 0) +
        (row.counts.ciuCount ?? 0) +
        (row.counts.althermaCount ?? 0), 0);
    const requirementMentionsMetering = includesAny(requirementText, [
        "meter",
        "metering",
        "mbus",
        "m-bus",
        "pulse"
    ]);
    const meteringRequired = requirementMentionsMetering || meterEndpointCount > 0;
    const meteringHardwareInScope = meteringRequired && !isMeteringHardwareOutOfTitanScope(meteringEvidenceText);
    const meteringProtocolExplicitlySpecified = meteringRequired && hasExplicitMeteringProtocol(meteringEvidenceText);
    const meteringProtocols = detectMeteringProtocols({
        requirementText: meteringEvidenceText,
        meteringRequired,
        meterEndpointCount
    });
    const meteringTopology = meteringRequired && meteringHardwareInScope && !meteringProtocolExplicitlySpecified
        ? "netmaster_direct"
        : detectMeteringTopology({
            requirementText: meteringEvidenceText,
            protocols: meteringProtocols
        });
    const meteringRequirementIds = approvedRequirements
        .filter((requirement) => {
        const text = `${requirement.type} ${requirement.interpretedText}`.toLowerCase();
        return includesAny(text, [
            "meter",
            "metering",
            "m-bus",
            "mbus",
            "modbus",
            "resident energy",
            "energy display",
            "tariff",
            "kwh"
        ]);
    })
        .map((requirement) => requirement.id);
    const remoteIoRequirementIds = approvedRequirements
        .filter((requirement) => hasRemoteIoLocalityCue(`${requirement.type} ${requirement.interpretedText}`.toLowerCase()))
        .map((requirement) => requirement.id);
    const thirdPartyAvIntegrationRequirements = approvedRequirements.filter(isThirdPartyAvIntegrationRequirement);
    const thirdPartyAvIntegrationRequired = thirdPartyAvIntegrationRequirements.length > 0;
    const thirdPartyAvIntegrationProtocol = thirdPartyAvIntegrationRequired
        ? detectThirdPartyAvIntegrationProtocol(thirdPartyAvIntegrationRequirements)
        : undefined;
    const triggers = {
        uiStrategy: uiStrategy.strategy,
        uiStrategyNeedsUserDecision: uiStrategy.needsUserDecision,
        uiStrategyRequirementIds: uiStrategy.requirementIds,
        remoteAppRequired: includesAny(requirementText, ["app", "remote", "smartphone"]),
        meteringRequired,
        meteringHardwareInScope,
        meteringProtocols,
        meteringTopology,
        meteringTopologyNeedsUserDecision: meteringRequired &&
            meteringHardwareInScope &&
            !meteringProtocolExplicitlySpecified,
        meteringBmsExposureRequired: meteringRequired && includesAny(requirementText, ["bms", "bacnet"]),
        ccsEnergyDisplayRequired: meteringRequired &&
            includesAny(requirementText, [
                "ccs",
                "resident energy",
                "energy display",
                "tariff",
                "kwh",
                "kw"
            ]),
        meteringRequirementIds,
        meteringMeterProducts: detectMeteringMeterProducts({
            evidenceText: meteringEvidenceText,
            topology: meteringTopology
        }),
        meterEndpointCount,
        bmsRequired: includesAny(nonEdgeRequirementText, ["bms", "bacnet", "modbus"]),
        centralMappingRequired: includesAny(requirementText, [
            "central control",
            "central location",
            "landlord",
            "head end",
            "netmaster"
        ]),
        alarmVisibilityRequired: includesAny(requirementText, ["alarm", "fault", "alert"]),
        schedulingRequired: includesAny(requirementText, [
            "schedule",
            "scheduling",
            "timeclock",
            "time clock"
        ]),
        centralScreenRequired: uiStrategy.centralScreenRequired,
        remoteIoPanelSuggested: remoteIoRequirementIds.length > 0,
        remoteIoRequirementIds,
        thirdPartyAvIntegrationRequired,
        ...(thirdPartyAvIntegrationProtocol ? { thirdPartyAvIntegrationProtocol } : {}),
        thirdPartyAvIntegrationRequirementIds: thirdPartyAvIntegrationRequirements.map((requirement) => requirement.id),
        edgeFeatures
    };
    const triggerKeys = Object.entries(triggers)
        .filter(([, value]) => value === true)
        .map(([key]) => key);
    const edgeAssumptions = edgeFeatures.length > 0
        ? [
            `Edge features detected: ${edgeFeatures.map((feature) => feature.featureKind).join(", ")}.`,
            ...edgeFeatures.map((feature) => `Edge ${feature.featureKind} input policy: ${feature.inputPolicy}.`)
        ]
        : [];
    const triggerAssumptions = triggerKeys.length > 0
        ? [`Requirement triggers detected: ${triggerKeys.join(", ")}.`]
        : [];
    const assumptions = triggerAssumptions.length > 0 || edgeAssumptions.length > 0
        ? [
            ...triggerAssumptions,
            ...(meteringRequired
                ? [
                    `Metering protocols: ${meteringProtocols.join(", ")}; topology: ${meteringTopology}; metering endpoints: ${meterEndpointCount}.`,
                    ...(!meteringHardwareInScope
                        ? [
                            "Metering evidence records pulse-only/by-others scope; no Titan gateway, interface module, EMS, or metering enclosure is derived."
                        ]
                        : meteringProtocolExplicitlySpecified
                            ? []
                            : [
                                "Metering assumed Modbus on NetMaster; requires Modbus cards in the meters; confirm or raise as RFI."
                            ])
                ]
                : []),
            ...edgeAssumptions,
            ...(thirdPartyAvIntegrationRequired
                ? [
                    `Third-party AV/home-automation integration protocol: ${thirdPartyAvIntegrationProtocol ?? "BACnet/IP"}.`
                ]
                : [])
        ]
        : [
            "No approved requirement triggers Netmaster, central panel, or IO module selection."
        ];
    const openDecisionRiskIds = uiStrategy.risk ? [uiStrategy.risk.id] : [];
    const strategyRuleTraceRefs = [
        "trace-controller-packing-start",
        "trace-central-panel-conditional",
        "trace-netmaster-no-hard-default",
        ...uiStrategy.ruleTraceRefs
    ];
    return {
        context: {
            id: `strategy-${projectId}-${apartmentTypeId}`,
            projectId,
            apartmentTypeId,
            catalogReleaseId,
            ruleSetId: rules.id,
            requirementBaselineId: `baseline-${projectId}-${apartmentTypeId}`,
            assumptions,
            openDecisionRiskIds,
            sourceRefs,
            ruleTraceRefs: strategyRuleTraceRefs
        },
        triggers,
        risks: uiStrategy.risk ? [uiStrategy.risk] : [],
        sourceRefs,
        ruleTraceRefs: strategyRuleTraceRefs
    };
}
export function uniqueSourceRefs(sourceRefs) {
    const seen = new Set();
    const result = [];
    for (const sourceRef of sourceRefs) {
        const key = JSON.stringify(sourceRef);
        if (!seen.has(key)) {
            seen.add(key);
            result.push(sourceRef);
        }
    }
    return result;
}
function includesAny(value, needles) {
    return needles.some((needle) => value.includes(needle));
}
function hasRemoteIoLocalityCue(text) {
    const namesRemoteIo = includesAny(text, [
        "remote io",
        "remote i/o",
        "remote relay",
        "remote enclosure",
        "relay enclosure",
        "local relay enclosure",
        "bathroom enclosure",
        "small enclosure"
    ]);
    const namesLoadArea = includesAny(text, [
        "bathroom",
        "ensuite",
        "towel rail",
        "towel-rail",
        "floor heating",
        "underfloor",
        "ufh",
        "eufh",
        "load relay"
    ]);
    const namesCablingConstraint = includesAny(text, [
        "cabling",
        "cable route",
        "cannot reach",
        "can't reach",
        "cant reach",
        "too far",
        "far from",
        "local enclosure"
    ]);
    return namesRemoteIo || (namesLoadArea && namesCablingConstraint);
}
function deriveUiStrategy(input) {
    const centralScreenRequirements = input.requirements.filter((requirement) => isCentralScreenRequirement(requirement));
    const centralOnlyRequirements = centralScreenRequirements.filter((requirement) => isCentralOnlyRequirement(requirement));
    const hasExplicitPerRoomUi = input.takeoffRows.some((row) => row.counts.thermostatUiPointCount !== undefined) ||
        input.requirements.some((requirement) => isPerRoomUiRequirement(requirement));
    const centralScreenRequired = input.rowCentralScreens > 0 || centralScreenRequirements.length > 0;
    const strategy = centralOnlyRequirements.length > 0
        ? "central_only"
        : centralScreenRequired
            ? "per_room_plus_central"
            : "per_room";
    const needsUserDecision = centralOnlyRequirements.length === 0 && !hasExplicitPerRoomUi;
    const requirementIds = centralOnlyRequirements.length > 0
        ? centralOnlyRequirements.map((requirement) => requirement.id)
        : centralScreenRequirements.map((requirement) => requirement.id);
    const ruleTraceRefs = strategy === "central_only"
        ? ["trace-ui-strategy-central-only"]
        : needsUserDecision
            ? ["trace-ui-strategy-default-needs-decision"]
            : [];
    const risk = needsUserDecision
        ? makeUiStrategyDecisionRisk({
            projectId: input.projectId,
            apartmentTypeId: input.apartmentTypeId,
            sourceRefs: input.sourceRefs,
            ruleTraceRefs
        })
        : undefined;
    return {
        strategy,
        centralScreenRequired: centralScreenRequired || strategy === "central_only",
        needsUserDecision,
        requirementIds,
        ruleTraceRefs,
        ...(risk ? { risk } : {})
    };
}
function isCentralScreenRequirement(requirement) {
    const text = requirementText(requirement);
    return (requirement.uiStrategy === "central_only" ||
        requirement.uiStrategy === "per_room_plus_central" ||
        requirement.type === "central_control_screen" ||
        (requirement.type === "ui_strategy" &&
            includesAny(text, [
                "central screen",
                "central control screen",
                "central control",
                "ccs",
                "ccs-7",
                "ts-7"
            ])) ||
        includesAny(text, [
            "central screen",
            "central control screen",
            "control from a central location",
            "ccs-7",
            "tp-ccs-7"
        ]));
}
function isCentralOnlyRequirement(requirement) {
    if (requirement.uiStrategy === "central_only") {
        return true;
    }
    const text = requirementText(requirement);
    const namesCentralScreen = isCentralScreenRequirement(requirement);
    const excludesPerRoomUi = includesAny(text, [
        "central-only",
        "central only",
        "central location only",
        "sole ui",
        "sole apartment ui",
        "only ui",
        "one screen per apartment, no",
        "no per-room",
        "no per room",
        "without per-room",
        "without per room",
        "no local stat",
        "no local stats",
        "no local thermostat",
        "no local thermostats",
        "no room stat",
        "no room stats",
        "no room thermostat",
        "no room thermostats",
        "no room screen",
        "no room screens",
        "controls the whole apartment",
        "whole-flat control"
    ]);
    return namesCentralScreen && excludesPerRoomUi;
}
function isPerRoomUiRequirement(requirement) {
    const text = requirementText(requirement);
    return (requirement.uiStrategy === "per_room" ||
        requirement.uiStrategy === "per_room_plus_central" ||
        [
            "ui_control_point_per_room",
            "takeoff_ui_point_count",
            "zone_ui_rdu_touch"
        ].includes(requirement.type) ||
        includesAny(text, [
            "control in every room",
            "ui/control point for every controlled room",
            "per room",
            "per-room",
            "local ui",
            "local room",
            "zone screen",
            "room display",
            "rdu",
            "thermostat symbols"
        ]));
}
function requirementText(requirement) {
    return `${requirement.type} ${requirement.interpretedText}`.toLowerCase();
}
function isThirdPartyAvIntegrationRequirement(requirement) {
    if (requirement.thirdPartyIntegration) {
        return true;
    }
    const text = requirementText(requirement);
    return ([
        "crestron",
        "crestron_interface",
        "third_party_av_integration",
        "home_automation_interface"
    ].includes(requirement.type) ||
        includesAny(text, [
            "crestron",
            "av integration",
            "a/v integration",
            "audio visual integration",
            "audio-visual integration",
            "home automation interface",
            "home-automation interface",
            "home automation integration",
            "third-party av",
            "third party av",
            "third-party home automation",
            "third party home automation"
        ]));
}
function detectThirdPartyAvIntegrationProtocol(requirements) {
    const explicitProtocol = requirements
        .map((requirement) => requirement.thirdPartyIntegration?.protocol?.trim())
        .find((protocol) => Boolean(protocol));
    if (explicitProtocol) {
        return explicitProtocol;
    }
    const text = requirements.map(requirementText).join("\n");
    if (includesAny(text, ["bacnet/ip", "bacnet ip", "bacnet over ip", "bacnet"])) {
        return "BACnet/IP";
    }
    if (includesAny(text, ["modbus tcp", "modbus/tcp"])) {
        return "Modbus TCP";
    }
    if (includesAny(text, ["ethernet", "tcp/ip", "tcp ip"])) {
        return "Ethernet";
    }
    return "BACnet/IP";
}
function makeUiStrategyDecisionRisk(input) {
    return {
        id: `risk-${input.apartmentTypeId}-ui-strategy-default`,
        projectId: input.projectId,
        type: "needsUserDecision",
        severity: "warning",
        scope: {
            level: "apartment_type",
            refId: input.apartmentTypeId
        },
        title: "UI strategy default needs confirmation",
        plainEnglish: "The approved inputs do not clearly choose per-room UI, per-room plus central UI, or central-only UI. The engine kept the per-room default for tender safety and recorded the decision.",
        technicalTrace: [...input.sourceRefs],
        recommendedActions: [
            {
                action: "Confirm whether this apartment type needs per-room UI, per-room UI plus a central screen, or central-only CCS/TS-7 control.",
                owner: "estimator",
                status: "open"
            }
        ],
        status: "open",
        blocksExport: false,
        sourceRefs: [...input.sourceRefs],
        ruleTraceRefs: [...input.ruleTraceRefs],
        createdAt: ENGINE_TIMESTAMP
    };
}
function detectEdgeFeatures(requirements) {
    const featureMap = new Map();
    for (const requirement of requirements) {
        const text = `${requirement.type} ${requirement.interpretedText}`.toLowerCase();
        const featureKind = detectEdgeFeatureKind(requirement, text);
        if (!featureKind) {
            continue;
        }
        const dedicatedSensorsPossible = includesAny(text, [
            "dedicated leak sensor",
            "dedicated sensors",
            "leak sensor",
            "leak rope",
            "leak-rope",
            "water sensor"
        ]);
        const inputPolicy = requirement.edgeFeature?.inputPolicy ??
            (dedicatedSensorsPossible
                ? "dedicated_sensors_to_confirm"
                : "reuse_existing_titan_inputs");
        const existing = featureMap.get(featureKind);
        if (existing) {
            existing.sourceRequirementIds.push(requirement.id);
            existing.dedicatedSensorsPossible =
                existing.dedicatedSensorsPossible || dedicatedSensorsPossible;
            if (existing.inputPolicy === "reuse_existing_titan_inputs") {
                existing.inputPolicy = inputPolicy;
            }
            continue;
        }
        featureMap.set(featureKind, {
            featureKind,
            sourceRequirementIds: [requirement.id],
            inputPolicy,
            dedicatedSensorsPossible
        });
    }
    return [...featureMap.values()].sort((left, right) => left.featureKind.localeCompare(right.featureKind));
}
function detectEdgeFeatureKind(requirement, text) {
    if (requirement.edgeFeature) {
        return requirement.edgeFeature.featureKind;
    }
    if (includesAny(text, ["leak detection", "leak-detection"]) ||
        (includesAny(text, ["edge", "gateway"]) && includesAny(text, ["leak"]))) {
        return "leak_detection";
    }
    if (includesAny(text, ["edge metering", "metering dashboard"]) ||
        (includesAny(text, ["edge"]) && includesAny(text, ["meter dashboard"]))) {
        return "metering_dashboard";
    }
    return undefined;
}
function detectMeteringProtocols(input) {
    if (!input.meteringRequired) {
        return [];
    }
    const protocols = new Set();
    if (includesAny(input.requirementText, ["wireless m-bus", "wireless mbus"])) {
        protocols.add("wireless_m_bus");
    }
    if (includesAny(input.requirementText, ["m-bus", "mbus", "m bus", "mb-10"])) {
        protocols.add("m_bus");
    }
    if (includesAny(input.requirementText, ["modbus"])) {
        protocols.add("modbus");
    }
    if (includesAny(input.requirementText, ["pulse", "pulsed"])) {
        protocols.add("pulse");
    }
    if (protocols.size === 0 && input.meterEndpointCount > 0) {
        protocols.add("modbus");
    }
    if (protocols.size === 0) {
        protocols.add("modbus");
    }
    return [...protocols].sort();
}
function hasExplicitMeteringProtocol(requirementText) {
    return includesAny(requirementText, [
        "wireless m-bus",
        "wireless mbus",
        "m-bus",
        "mbus",
        "m bus",
        "mb-10",
        "modbus",
        "rs485",
        "pulse",
        "pulsed"
    ]);
}
function isMeteringHardwareOutOfTitanScope(evidenceText) {
    const hasPulseOnlyInputEvidence = /\b(?:pulse(?:d)?[- ]only|only\s+(?:an?\s+)?(?:electric\s+)?pulse(?:d)?\s+meter(?:ing)?\s+input|(?:electric\s+)?pulse(?:d)?\s+meter(?:ing)?\s+input\s+is\s+verified)\b/.test(evidenceText);
    const explicitlyNoTitanMeteringHardware = /\bno\s+titan\s+metering\s+(?:hardware|gateway|interface|interface module)\b/.test(evidenceText) ||
        /\bno\s+by[- ]titan\s+(?:bus\s+)?gateway\b/.test(evidenceText) ||
        /\bno\s+by[- ]titan\s+.*\bstandalone\s+metering\s+hardware\b/.test(evidenceText);
    return hasPulseOnlyInputEvidence || explicitlyNoTitanMeteringHardware;
}
function detectMeteringTopology(input) {
    const hasModbus = input.protocols.includes("modbus");
    const namesNetmaster = includesAny(input.requirementText, [
        "netmaster",
        "net master",
        "tp-nm-r-em-d",
        "netmaster 2000"
    ]);
    const directToNetmaster = includesAny(input.requirementText, [
        "netmaster-direct",
        "netmaster direct",
        "onto the netmaster",
        "on the netmaster",
        "to the netmaster",
        "read by the apartment netmaster",
        "read by netmaster",
        "netmaster reads",
        "netmaster is the modbus master",
        "modbus master",
        "bus master",
        "rs485 daisy-chain",
        "rs485 daisy chain",
        "daisy-chain",
        "daisy chain"
    ]) && namesNetmaster;
    if (hasModbus && directToNetmaster) {
        return "netmaster_direct";
    }
    return "central_gateway";
}
function detectMeteringMeterProducts(input) {
    if (input.topology !== "netmaster_direct") {
        return [];
    }
    const products = [];
    if (includesAny(input.evidenceText, ["em112", "evinox electricity"])) {
        products.push({
            productPartNo: "EM112",
            family: "By-others Modbus meters",
            role: "by_others_electricity_meter",
            quantity: 1,
            protocols: ["Modbus RTU", "RS485"],
            connectivity: "Evinox EM112 electricity meter sits on the apartment Modbus RTU RS485 bus read by TP-NM-R-EM-D."
        });
    }
    if (includesAny(input.evidenceText, [
        "modusat",
        "modu sat",
        "modusat-xr",
        "modusat xr",
        "xr-chhc"
    ])) {
        products.push({
            productPartNo: "MODUSAT-XR-CHHC",
            family: "By-others Modbus meters",
            role: "by_others_heat_cool_meter",
            quantity: 1,
            protocols: ["Modbus RTU", "RS485"],
            connectivity: "Evinox ModuSat XR CHHC heat/cool meter functions sit on the apartment Modbus RTU RS485 bus read by TP-NM-R-EM-D."
        });
    }
    return products;
}
//# sourceMappingURL=strategyContext.js.map